import { calculatePremium } from "../../../globals/utils/commons";
import {
  parseCurrencyToNumber,
  recalcGuardianshipPremiumFromBasicInfo,
} from "./formHelpers";
import logger from "../../../globals/utils/logger";

export function calculateAgeFromDate(dateOfBirth) {
  const birthDate = new Date(dateOfBirth);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
}

export function hydrateBasicInfoFromStorage(
  storageKey,
  targetLabel,
  currentValue,
  setter,
  logContext
) {
  if (!targetLabel) return;
  if (currentValue?.label === targetLabel) return;
  const saved = localStorage.getItem(storageKey);
  if (!saved) return;
  try {
    const parsed = JSON.parse(saved);
    if (parsed.label === targetLabel) {
      setter(parsed);
    }
  } catch (err) {
    logger.debug(logContext, "localStorage selection sync failed", { err, storageKey });
  }
}

export function applyStandardPremium(
  updatedData,
  defaultCountField,
  esignatureMode,
  esignatureThreshold
) {
  if (!updatedData.bondAmount) return null;
  const bondAmount = parseCurrencyToNumber(updatedData.bondAmount);
  if (!Number.isFinite(bondAmount)) return null;
  const premium = calculatePremium(bondAmount);
  const next = {
    ...updatedData,
    premiumAmount: premium,
    [defaultCountField]: "1",
  };
  if (
    esignatureMode === "always" ||
    (esignatureMode === "threshold" && bondAmount < esignatureThreshold)
  ) {
    next.is_esignatures = "No";
  }
  return next;
}

export function applyGuardianshipPremium(updatedData, defaultCountField) {
  if (!updatedData.bondAmount || !updatedData.dateOfDeath) return null;
  const premium = recalcGuardianshipPremiumFromBasicInfo({
    bondAmount: updatedData.bondAmount,
    dateOfDeath: updatedData.dateOfDeath,
    bondSubCategory: updatedData.bondSubCategory,
  });
  if (premium === null) return null;
  return {
    ...updatedData,
    premiumAmount: premium,
    [defaultCountField]: "1",
  };
}

export function recalcBasicInfoPremium(
  updatedData,
  { premiumStrategy, defaultCountField, esignatureMode, esignatureThreshold = 50001 },
  withDefaultCount = true
) {
  if (premiumStrategy === "guardianship") {
    if (!updatedData.bondAmount || !updatedData.dateOfDeath) return null;
    const premium = recalcGuardianshipPremiumFromBasicInfo({
      bondAmount: updatedData.bondAmount,
      dateOfDeath: updatedData.dateOfDeath,
      bondSubCategory: updatedData.bondSubCategory,
    });
    if (premium === null) return null;
    const next = { ...updatedData, premiumAmount: premium };
    if (withDefaultCount) next[defaultCountField] = "1";
    return next;
  }
  return applyStandardPremium(
    updatedData,
    defaultCountField,
    esignatureMode,
    esignatureThreshold
  );
}

function parseStoredSelection(raw, logContext, storageKey) {
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch (err) {
    logger.debug(logContext, `${storageKey} JSON parse failed`, { err });
    return null;
  }
}

function hydrateHistoricBasicInfoOnMount({
  config,
  data,
  updateData,
  setState,
  setType,
}) {
  const parsedState = parseStoredSelection(
    localStorage.getItem("selectedState"),
    config.logContext,
    "selectedState"
  );
  if (parsedState) {
    if (config.showCounty) setState(parsedState);
    updateData({ ...data, state: parsedState.label });
  }

  const parsedType = parseStoredSelection(
    localStorage.getItem("selectedBondType"),
    config.logContext,
    "selectedBondType"
  );
  if (parsedType) {
    updateData({ ...data, bondType: parsedType.label });
  }
}

function hydrateCreationBasicInfoOnMount({ logContext, state, setState, type, setType }) {
  if (!state) {
    const parsedState = parseStoredSelection(
      localStorage.getItem("selectedState"),
      logContext,
      "selectedState"
    );
    if (parsedState) setState(parsedState);
  }
  if (!type) {
    const parsedType = parseStoredSelection(
      localStorage.getItem("selectedBondType"),
      logContext,
      "selectedBondType"
    );
    if (parsedType) setType(parsedType);
  }
}

/** One-time mount hydration for creation vs historic basic-info surfaces. */
export function hydrateBasicInfoOnMount({
  surface,
  config,
  data,
  updateData,
  state,
  setState,
  type,
  setType,
}) {
  if (surface === "historic") {
    hydrateHistoricBasicInfoOnMount({ config, data, updateData, setState, setType });
    return;
  }
  hydrateCreationBasicInfoOnMount({ logContext: config.logContext, state, setState, type, setType });
}
